import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {Abwesenheit} from "../../api";
import DataTable from "../data/DataTable";
import useCustomFilters from "../data/useCustomFilters";

export type MeineAbwesenheitenFilter = {
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<MeineAbwesenheitenFilter>, "filter"> &
    Partial<Pick<DataRequest<MeineAbwesenheitenFilter>, "filter">>;

const tableName: string = "meine-abwesenheiten"

export default function MyAbwesenheitDataTable({...input}: Props) {
    const request = useDataRequest<MeineAbwesenheitenFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp request={request} filteredStateFilter={filteredState}/>
            <MyAbwesenheitResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<MeineAbwesenheitenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<MeineAbwesenheitenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-meine-abwesenheit");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-meine-abwesenheit");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function MyAbwesenheitResults({
                                  request,
                                  setFilteredState,
                              }: {
    request: DataRequestState<MeineAbwesenheitenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<MeineAbwesenheitenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadUrlaub: DataLoader<MeineAbwesenheitenFilter, Abwesenheit> = useCallback(
        async (params) =>
            (await getApi()).abwesenheiten.sucheMeineAbwesenheiten({...params}),
        [getApi]
    );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadUrlaub}
            onRowClick={(row, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open("/meine-abwesenheit/" + row.id, "_blank");
                } else {
                    navigate("/meine-abwesenheit/" + row.id);
                }
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Abwesenheit>> {
    const {t: translation} = useT("abwesenheit");

    return useMemo(
        () => [
            {
                field: "type",
                headerName: capitalize(translation("abwesenheit-attribute-type")),
            },
            {
                field: "start",
                headerName: capitalize(translation("abwesenheit-attribute-start")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString();
                    }
                    return "";
                },
            },
            {
                field: "ende",
                headerName: capitalize(translation("abwesenheit-attribute-end")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString();
                    }
                    return "";
                },
            },
            {
                field: "tage",
                headerName: capitalize(translation("abwesenheit-attribute-days")),
                align: "right",
                headerAlign: "right",
                type: "number",
            },
            {
                field: "erstelltAm",
                headerName: capitalize(translation("abwesenheit-attribute-created-at")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString();
                    }
                    return "";
                },
            },
            {
                field: "bemerkung",
                headerName: capitalize(translation("abwesenheit-attribute-remark")),
            },
        ],
        [translation]
    );
}
